import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardSubtitle,
  CardTitle,
  Combobox,
  Field,
  Input,
  Select,
  useToast,
} from '@keobook/ui';
import { type IncomeStatement, type TrialBalance } from '@keobook/shared';
import { useMoneyFormatter } from '../lib/useCurrency';
import { api } from '../lib/api';
import { Layout } from '../components/Layout';
import { DrillDownPanel, type DrillTarget } from '../components/DrillDownPanel';
import { ReportComparison } from '../components/ReportComparison';
import {
  todayIso,
  firstOfMonth,
  monthRange,
  yearRange,
  monthsBetween,
  yearsBetween,
  buildPresets,
  type PeriodBounds,
} from '../lib/reportPeriods';

type GlAccount = { id: string; code: string; name: string; kind: string };
type GlResp = Awaited<ReturnType<typeof api.getGlDetail>>;
type BalanceSheet = Awaited<ReturnType<typeof api.getBalanceSheet>>;
type GlSummary = Awaited<ReturnType<typeof api.getGlSummary>>;

type ReportView = 'all' | 'pl' | 'tb' | 'bs' | 'cf' | 'gl' | 'ar';

const VIEW_TABS: Array<{ id: ReportView; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'pl', label: 'Profit & Loss' },
  { id: 'tb', label: 'Trial Balance' },
  { id: 'bs', label: 'Balance Sheet' },
  { id: 'cf', label: 'Cash Flows' },
  { id: 'gl', label: 'General Ledger' },
  { id: 'ar', label: 'A/R Aging' },
];

const AR_BUCKETS: Array<{ key: string; label: string }> = [
  { key: 'current', label: 'Current' },
  { key: '1-30', label: '1–30 days' },
  { key: '31-60', label: '31–60 days' },
  { key: '61-90', label: '61–90 days' },
  { key: '90+', label: '90+ days' },
];

type ArAgingResp = Awaited<ReturnType<typeof api.getArAging>>;

type CashFlowResp = Awaited<ReturnType<typeof api.getCashFlow>>;
type MonthlySummaryResp = Awaited<ReturnType<typeof api.getMonthlySummary>>;
type ColumnarPlResp = Awaited<ReturnType<typeof api.getIncomeStatementByMonth>>;

export function Reports() {
  const fmt = useMoneyFormatter();
  // Defaults stay empty until period-bounds resolves; once it does we set the
  // initial range to cover everything the tenant has posted (earliest entry →
  // today) so a customer who only uploaded a historical statement isn't
  // greeted with an empty "this month" view.
  const [bounds, setBounds] = useState<PeriodBounds | null>(null);
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  // Brickstone asked to process statements separately so big books don't pay
  // four-report cost on every Period change. "All" preserves the previous
  // behavior; single-tab views fetch only the selected statement.
  const [view, setView] = useState<ReportView>('all');
  // Period-over-period comparison mode: swaps the single-report body for a
  // source-vs-target diff view (any report, any two free-form periods).
  const [compareMode, setCompareMode] = useState(false);
  const [is, setIs] = useState<IncomeStatement | null>(null);
  const [tb, setTb] = useState<TrialBalance | null>(null);
  const [bs, setBs] = useState<BalanceSheet | null>(null);
  const [cf, setCf] = useState<CashFlowResp | null>(null);
  const [monthly, setMonthly] = useState<MonthlySummaryResp | null>(null);
  const [columnarPl, setColumnarPl] = useState<ColumnarPlResp | null>(null);
  const [glSum, setGlSum] = useState<GlSummary | null>(null);
  const [ar, setAr] = useState<ArAgingResp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // QuickBooks-style drill-down. When non-null, an overlay modal shows the
  // journal lines that composed the figure the user clicked. Today supports
  // account-level drill — section subtotals + grand totals open the panel
  // for an aggregate label but render "no posted lines" because gl-detail
  // is single-account. Multi-account drill is a follow-up.
  const [drill, setDrill] = useState<DrillTarget | null>(null);
  // "Prepared for Accountant Review" letterhead — when on, every export
  // gets a top-of-document cover band with the SSARS-21-style disclosure.
  // Persisted across sessions so a reviewer who always wants the cover
  // doesn't re-check the box each time. Defaults on — matches the
  // STARLINX deliverable Brickstone gave as the carbon-copy template.
  const [letterhead, setLetterhead] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem('keobook:reports:letterhead');
      return v == null ? true : v === '1';
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('keobook:reports:letterhead', letterhead ? '1' : '0');
    } catch {
      /* private mode / quota — non-fatal */
    }
  }, [letterhead]);

  useEffect(() => {
    let alive = true;
    api
      .getReportPeriodBounds()
      .then((b) => {
        if (!alive) return;
        setBounds(b);
        const today = todayIso();
        setFrom(b.earliest ?? firstOfMonth());
        setTo(today);
      })
      .catch(() => {
        if (!alive) return;
        // If bounds is unreachable (older API revision, network blip), fall
        // back to the conservative current-month default rather than blocking
        // the page.
        setBounds({ earliest: null, latest: null });
        setFrom(firstOfMonth());
        setTo(todayIso());
      });
    return () => {
      alive = false;
    };
  }, []);

  const invalidRange = useMemo(() => {
    if (!from || !to) return null; // waiting for bounds to land — don't show an error
    if (from > to) return 'The "from" date must be on or before the "to" date.';
    return null;
  }, [from, to]);

  useEffect(() => {
    if (!from || !to || invalidRange) {
      if (invalidRange) setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    // Fetch only what the current view needs. "All" still fetches all four;
    // a single-tab view fetches one. Reports already loaded for hidden tabs
    // stay in state — they may go stale if the user navigates back to them
    // after changing dates, but date changes also re-trigger this effect.
    const tasks: Array<Promise<unknown>> = [];
    if (view === 'all' || view === 'pl') {
      tasks.push(api.getIncomeStatement({ from, to }).then((r) => alive && setIs(r)));
    }
    if (view === 'all' || view === 'tb') {
      // Pass `from` alongside `asOf` so the Period picker also bounds
      // TB: a window that excludes all activity returns an empty
      // statement instead of carrying earlier balances forward.
      tasks.push(api.getTrialBalance({ asOf: to, from }).then((r) => alive && setTb(r)));
    }
    if (view === 'all' || view === 'bs') {
      tasks.push(api.getBalanceSheet({ asOf: to, from }).then((r) => alive && setBs(r)));
    }
    if (view === 'all' || view === 'cf') {
      tasks.push(api.getCashFlow({ from, to }).then((r) => alive && setCf(r)));
    }
    if (view === 'all' || view === 'gl') {
      tasks.push(api.getGlSummary({ from, to }).then((r) => alive && setGlSum(r)));
    }
    if (view === 'all' || view === 'ar') {
      // A/R aging is point-in-time; the Period card's "to" date becomes asOf.
      tasks.push(api.getArAging({ asOf: to }).then((r) => alive && setAr(r)));
    }
    // The monthly summary table only matters on the "All" tab. Single-
    // statement views render their own per-account detail.
    if (view === 'all') {
      tasks.push(api.getMonthlySummary({ from, to }).then((r) => alive && setMonthly(r)));
      tasks.push(
        api.getIncomeStatementByMonth({ from, to }).then((r) => alive && setColumnarPl(r)),
      );
    }
    Promise.all(tasks)
      .catch(
        (err: unknown) =>
          alive && setError(err instanceof Error ? err.message : 'Could not load reports.'),
      )
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [view, from, to, invalidRange]);

  const presets = useMemo(() => buildPresets(bounds), [bounds]);
  const activePresetId = presets.find((p) => p.range.from === from && p.range.to === to)?.id;

  return (
    <Layout>
      <div className="kb-page" style={{ maxWidth: 820, margin: '0 auto', padding: '2rem 1.5rem' }}>
        <h1
          style={{
            fontSize: 26,
            fontWeight: 600,
            color: 'var(--kb-gray-900)',
            margin: '0 0 1.5rem',
          }}
        >
          Reports
        </h1>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
          <Button
            size="sm"
            variant={compareMode ? 'primary' : 'ghost'}
            aria-pressed={compareMode}
            onClick={() => setCompareMode((v) => !v)}
          >
            {compareMode ? 'Single report' : 'Compare periods'}
          </Button>
        </div>

        {compareMode ? (
          <ReportComparison bounds={bounds} />
        ) : (
          <>
            <Card>
              <CardTitle>Period</CardTitle>
              <CardSubtitle>
                One date range drives every report below. Profit &amp; Loss and General Ledger use
                the full range; Trial Balance and Balance Sheet are taken as of the &ldquo;to&rdquo;
                date.
                {bounds?.earliest && (
                  <>
                    {' '}
                    Earliest posted entry: <strong>{bounds.earliest}</strong>.
                  </>
                )}
              </CardSubtitle>

              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  flexWrap: 'wrap',
                  marginTop: '0.875rem',
                }}
              >
                {presets.map((p) => (
                  <Button
                    key={p.id}
                    size="sm"
                    variant={activePresetId === p.id ? 'primary' : 'ghost'}
                    aria-pressed={activePresetId === p.id}
                    onClick={() => {
                      setFrom(p.range.from);
                      setTo(p.range.to);
                    }}
                  >
                    {p.label}
                  </Button>
                ))}
              </div>

              {/* Specific month / year quick-picks. The Period presets above are
              all anchored to today — Brickstone's data is in Jan 2026 and she
              had no one-click way to land on a Jan-only view. These two
              dropdowns list every month/year between earliest posted activity
              and today so any specific month or any covered year is a single
              selection away. */}
              {bounds?.earliest && (
                <div
                  style={{
                    marginTop: '1.125rem',
                    paddingTop: '1rem',
                    borderTop: '1px solid var(--kb-gray-200)',
                  }}
                >
                  <div
                    className="kb-muted"
                    style={{ fontSize: 13, marginBottom: '0.5rem', fontWeight: 500 }}
                  >
                    Specific period
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      gap: 12,
                      flexWrap: 'wrap',
                      alignItems: 'flex-end',
                    }}
                  >
                    <Field
                      label="Pick a month"
                      hint="Type to filter — e.g. &ldquo;dec&rdquo; for every December."
                    >
                      <Combobox
                        placeholder="— Select a month —"
                        width={220}
                        value={(() => {
                          if (!from || !to) return '';
                          const candidate = from.slice(0, 7);
                          const r = monthRange(candidate);
                          return r.from === from && r.to === to ? candidate : '';
                        })()}
                        options={monthsBetween(bounds.earliest, todayIso())}
                        onChange={(v) => {
                          const r = monthRange(v);
                          setFrom(r.from);
                          setTo(r.to);
                        }}
                      />
                    </Field>
                    <Field label="Pick a year">
                      <Select
                        value={(() => {
                          if (!from || !to) return '';
                          const y = from.slice(0, 4);
                          const r = yearRange(Number(y));
                          return r.from === from && r.to === to ? y : '';
                        })()}
                        onChange={(e) => {
                          const v = e.currentTarget.value;
                          if (!v) return;
                          const r = yearRange(Number(v));
                          setFrom(r.from);
                          setTo(r.to);
                        }}
                      >
                        <option value="">— Select a year —</option>
                        {yearsBetween(bounds.earliest, todayIso()).map((y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  </div>
                </div>
              )}

              <div
                style={{
                  marginTop: '1.125rem',
                  paddingTop: '1rem',
                  borderTop: '1px solid var(--kb-gray-200)',
                }}
              >
                <div
                  className="kb-muted"
                  style={{ fontSize: 13, marginBottom: '0.5rem', fontWeight: 500 }}
                >
                  Custom range
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: 12,
                    flexWrap: 'wrap',
                    alignItems: 'flex-end',
                  }}
                >
                  <Field label="From">
                    <Input
                      type="date"
                      value={from}
                      max={to || undefined}
                      onChange={(e) => setFrom(e.currentTarget.value)}
                    />
                  </Field>
                  <Field label="To">
                    <Input
                      type="date"
                      value={to}
                      min={from || undefined}
                      onChange={(e) => setTo(e.currentTarget.value)}
                    />
                  </Field>
                </div>
              </div>

              <div
                style={{
                  marginTop: '1.125rem',
                  paddingTop: '1rem',
                  borderTop: '1px solid var(--kb-gray-200)',
                }}
              >
                <div
                  className="kb-muted"
                  style={{ fontSize: 13, marginBottom: '0.5rem', fontWeight: 500 }}
                >
                  Export formatting
                </div>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    fontSize: 14,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={letterhead}
                    onChange={(e) => setLetterhead(e.currentTarget.checked)}
                    style={{ marginTop: 2 }}
                  />
                  <span>
                    Include &ldquo;Prepared for Accountant Review&rdquo; letterhead on downloads
                    <div className="kb-muted" style={{ fontSize: 12, marginTop: 2 }}>
                      Adds a cover band to every PDF / XLSX / CSV export with a standard SSARS-21
                      &ldquo;no assurance provided&rdquo; disclosure.
                    </div>
                  </span>
                </label>
              </div>

              {invalidRange && (
                <div role="alert" className="kb-form-error" style={{ marginTop: 10 }}>
                  {invalidRange}
                </div>
              )}
              {loading && !invalidRange && (
                <p className="kb-muted" style={{ marginTop: 10 }}>
                  Updating reports…
                </p>
              )}
            </Card>

            {error && (
              <div role="alert" className="kb-form-error" style={{ marginTop: '1.25rem' }}>
                {error}
              </div>
            )}

            {/* Statement selector — pick which financial statement(s) to render */}
            <div
              role="tablist"
              aria-label="Report view"
              style={{
                display: 'flex',
                gap: 8,
                flexWrap: 'wrap',
                marginTop: '1.25rem',
                marginBottom: '0.25rem',
              }}
            >
              {VIEW_TABS.map((t) => (
                <Button
                  key={t.id}
                  size="sm"
                  variant={view === t.id ? 'primary' : 'ghost'}
                  role="tab"
                  aria-selected={view === t.id}
                  onClick={() => setView(t.id)}
                >
                  {t.label}
                </Button>
              ))}
            </div>

            {!invalidRange && loading && !is && !tb && !bs && !cf && !glSum && !ar && (
              <div style={{ marginTop: '1.25rem' }}>
                <Card>
                  <CardSubtitle>Loading reports…</CardSubtitle>
                </Card>
              </div>
            )}

            {!invalidRange && (is || tb || bs || cf || glSum || ar) && (
              <>
                {view === 'all' && is && tb && bs && cf && (
                  <div style={{ marginTop: '1.25rem' }}>
                    <Card>
                      <CardTitle>Download all financial statements</CardTitle>
                      <CardSubtitle>
                        One file containing the Statement of Operations, Statement of Financial
                        Position, Statement of Cash Flows, and Trial Balance — formatted as a CPA
                        firm would present them.
                      </CardSubtitle>
                      <ExportButtons
                        name="all"
                        params={{ from, to, asOf: to, letterhead }}
                        disabled={false}
                      />
                    </Card>
                  </div>
                )}

                {view === 'all' && monthly && monthly.months.length > 1 && (
                  <MonthlySummaryCards monthly={monthly} />
                )}

                {view === 'all' && columnarPl && columnarPl.months.length > 1 && (
                  <ColumnarPlCard data={columnarPl} from={from} to={to} letterhead={letterhead} />
                )}

                {(view === 'all' || view === 'pl') && (
                  <div style={{ marginTop: '1.25rem' }}>
                    <Card>
                      <CardTitle>Profit &amp; Loss</CardTitle>
                      <CardSubtitle>
                        {is ? `${is.from} to ${is.to}` : `${from} to ${to}`} — Income Statement
                      </CardSubtitle>
                      <ExportButtons
                        name="income-statement"
                        params={{ from, to, letterhead }}
                        disabled={!is}
                      />
                      {is &&
                      is.revenue.length === 0 &&
                      is.costOfRevenue.length === 0 &&
                      is.expenses.length === 0 ? (
                        <p className="kb-muted" style={{ marginTop: '1rem' }}>
                          No activity in this period yet.
                        </p>
                      ) : (
                        is && (
                          <table className="kb-report">
                            <tbody>
                              <tr className="kb-report__section">
                                <td colSpan={2}>Revenue</td>
                              </tr>
                              {is.revenue.map((r) => (
                                <tr
                                  key={r.accountId}
                                  className="kb-report__row--drill"
                                  onClick={() =>
                                    setDrill({
                                      accountId: r.accountId,
                                      label: `${r.code} ${r.name}`,
                                      from,
                                      to,
                                    })
                                  }
                                  title="Click to drill into this account"
                                >
                                  <td>
                                    <span className="kb-report__code">{r.code}</span> {r.name}
                                  </td>
                                  <td className="kb-report__amt">{fmt(r.amountCents)}</td>
                                </tr>
                              ))}
                              <tr
                                className="kb-report__subtotal kb-report__row--drill"
                                onClick={() =>
                                  setDrill({
                                    accountIds: is.revenue.map((r) => r.accountId),
                                    label: 'Total revenue',
                                    from,
                                    to,
                                  })
                                }
                                title="Click to see every line in revenue"
                              >
                                <td>Total revenue</td>
                                <td className="kb-report__amt">
                                  {fmt(is.totalRevenueCents)}
                                </td>
                              </tr>

                              {/* Cost of Revenue + Gross Profit — rendered only when
                          the tenant has at least one cost-of-revenue
                          account in use this period. Service-only books
                          skip straight from Revenue to Operating Expenses
                          so the report stays clean. */}
                              {(is.costOfRevenue.length > 0 ||
                                is.totalCostOfRevenueCents !== 0) && (
                                <>
                                  <tr className="kb-report__section">
                                    <td colSpan={2}>Cost of Revenue</td>
                                  </tr>
                                  {is.costOfRevenue.map((r) => (
                                    <tr
                                      key={r.accountId}
                                      className="kb-report__row--drill"
                                      onClick={() =>
                                        setDrill({
                                          accountId: r.accountId,
                                          label: `${r.code} ${r.name}`,
                                          from,
                                          to,
                                        })
                                      }
                                      title="Click to drill into this account"
                                    >
                                      <td>
                                        <span className="kb-report__code">{r.code}</span> {r.name}
                                      </td>
                                      <td className="kb-report__amt">
                                        {fmt(r.amountCents)}
                                      </td>
                                    </tr>
                                  ))}
                                  <tr
                                    className="kb-report__subtotal kb-report__row--drill"
                                    onClick={() =>
                                      setDrill({
                                        accountIds: is.costOfRevenue.map((r) => r.accountId),
                                        label: 'Total cost of revenue',
                                        from,
                                        to,
                                      })
                                    }
                                    title="Click to see every line in cost of revenue"
                                  >
                                    <td>Total cost of revenue</td>
                                    <td className="kb-report__amt">
                                      {fmt(is.totalCostOfRevenueCents)}
                                    </td>
                                  </tr>
                                  <tr
                                    className="kb-report__total kb-report__row--drill"
                                    onClick={() =>
                                      setDrill({
                                        accountIds: [
                                          ...is.revenue.map((r) => r.accountId),
                                          ...is.costOfRevenue.map((r) => r.accountId),
                                        ],
                                        label: 'Gross profit (revenue − cost of revenue)',
                                        from,
                                        to,
                                      })
                                    }
                                    title="Click to see every line behind gross profit"
                                  >
                                    <td>Gross profit</td>
                                    <td className="kb-report__amt">
                                      {fmt(is.grossProfitCents)}
                                    </td>
                                  </tr>
                                </>
                              )}

                              <tr className="kb-report__section">
                                <td colSpan={2}>Operating Expenses</td>
                              </tr>
                              {is.expenses.map((r) => (
                                <tr
                                  key={r.accountId}
                                  className="kb-report__row--drill"
                                  onClick={() =>
                                    setDrill({
                                      accountId: r.accountId,
                                      label: `${r.code} ${r.name}`,
                                      from,
                                      to,
                                    })
                                  }
                                  title="Click to drill into this account"
                                >
                                  <td>
                                    <span className="kb-report__code">{r.code}</span> {r.name}
                                  </td>
                                  <td className="kb-report__amt">{fmt(r.amountCents)}</td>
                                </tr>
                              ))}
                              <tr
                                className="kb-report__subtotal kb-report__row--drill"
                                onClick={() =>
                                  setDrill({
                                    accountIds: is.expenses.map((r) => r.accountId),
                                    label: 'Total operating expenses',
                                    from,
                                    to,
                                  })
                                }
                                title="Click to see every line in operating expenses"
                              >
                                <td>Total operating expenses</td>
                                <td className="kb-report__amt">
                                  {fmt(is.totalExpensesCents)}
                                </td>
                              </tr>

                              <tr
                                className="kb-report__total kb-report__row--drill"
                                onClick={() =>
                                  setDrill({
                                    accountIds: [
                                      ...is.revenue.map((r) => r.accountId),
                                      ...is.costOfRevenue.map((r) => r.accountId),
                                      ...is.expenses.map((r) => r.accountId),
                                    ],
                                    label: 'Net income (every P&L line in period)',
                                    from,
                                    to,
                                  })
                                }
                                title="Click to see every line behind net income"
                              >
                                <td>Net income (loss)</td>
                                <td className="kb-report__amt">{fmt(is.netIncomeCents)}</td>
                              </tr>
                            </tbody>
                          </table>
                        )
                      )}
                      <ReportNotes reportKind="income_statement" from={from} to={to} />
                    </Card>
                  </div>
                )}

                {(view === 'all' || view === 'tb') && (
                  <div style={{ marginTop: '1.25rem' }}>
                    <Card>
                      <CardTitle>Trial Balance</CardTitle>
                      <ExportButtons
                        name="trial-balance"
                        params={{ asOf: to, from, letterhead }}
                        disabled={!tb}
                      />
                      <CardSubtitle>
                        {tb ? `As of ${tb.asOf}` : ''}
                        {tb && tb.rows.length > 0 && (
                          <>
                            {' · '}
                            {tb.balanced ? (
                              <span className="kb-pill kb-pill--ok">Balanced</span>
                            ) : (
                              <span className="kb-pill kb-pill--warn">Out of balance</span>
                            )}
                          </>
                        )}
                      </CardSubtitle>
                      {tb && tb.rows.length === 0 ? (
                        <p className="kb-muted" style={{ marginTop: '1rem' }}>
                          No posted entries yet.
                        </p>
                      ) : (
                        tb && (
                          <table className="kb-report">
                            <thead>
                              <tr>
                                <th>Account</th>
                                <th className="kb-report__amt">Debit</th>
                                <th className="kb-report__amt">Credit</th>
                              </tr>
                            </thead>
                            <tbody>
                              {tb.rows.map((r) => (
                                <tr key={r.accountId}>
                                  <td>
                                    <span className="kb-report__code">{r.code}</span> {r.name}
                                  </td>
                                  <td className="kb-report__amt">
                                    {r.debitCents ? fmt(r.debitCents) : ''}
                                  </td>
                                  <td className="kb-report__amt">
                                    {r.creditCents ? fmt(r.creditCents) : ''}
                                  </td>
                                </tr>
                              ))}
                              <tr className="kb-report__total">
                                <td>Totals</td>
                                <td className="kb-report__amt">
                                  {fmt(tb.totalDebitCents)}
                                </td>
                                <td className="kb-report__amt">
                                  {fmt(tb.totalCreditCents)}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        )
                      )}
                    </Card>
                  </div>
                )}

                {(view === 'all' || view === 'bs') && (
                  /* Balance Sheet */
                  <div style={{ marginTop: '1.25rem' }}>
                    <Card>
                      <CardTitle>Balance Sheet</CardTitle>
                      <ExportButtons
                        name="balance-sheet"
                        params={{ asOf: to, from, letterhead }}
                        disabled={!bs}
                      />
                      <CardSubtitle>
                        As of {bs?.asOf ?? '—'}
                        {bs &&
                          bs.assets.length + bs.liabilities.length + bs.equity.length > 0 &&
                          (bs.balanced ? (
                            <span className="kb-pill kb-pill--ok" style={{ marginLeft: 8 }}>
                              Balanced
                            </span>
                          ) : (
                            <span className="kb-pill kb-pill--warn" style={{ marginLeft: 8 }}>
                              Out of balance
                            </span>
                          ))}
                      </CardSubtitle>
                      {bs &&
                      bs.assets.length === 0 &&
                      bs.liabilities.length === 0 &&
                      bs.equity.length === 0 ? (
                        <p className="kb-muted" style={{ marginTop: '1rem' }}>
                          No posted balances yet.
                        </p>
                      ) : (
                        bs && (
                          <table className="kb-report">
                            <tbody>
                              <tr className="kb-report__section">
                                <td colSpan={2}>Assets</td>
                              </tr>
                              {bs.assets.map((r) => (
                                <tr
                                  key={r.accountId}
                                  className="kb-report__row--drill"
                                  onClick={() =>
                                    setDrill({
                                      accountId: r.accountId,
                                      label: `${r.code} ${r.name}`,
                                      from: from || (bs.asOf ?? to),
                                      to: bs.asOf ?? to,
                                    })
                                  }
                                  title="Click to drill into this account"
                                >
                                  <td>
                                    <span className="kb-report__code">{r.code}</span> {r.name}
                                  </td>
                                  <td className="kb-report__amt">{fmt(r.amountCents)}</td>
                                </tr>
                              ))}
                              <tr
                                className="kb-report__subtotal kb-report__row--drill"
                                onClick={() =>
                                  setDrill({
                                    accountIds: bs.assets.map((r) => r.accountId),
                                    label: 'Total assets',
                                    from: from || (bs.asOf ?? to),
                                    to: bs.asOf ?? to,
                                  })
                                }
                                title="Click to see every line in assets"
                              >
                                <td>Total assets</td>
                                <td className="kb-report__amt">
                                  {fmt(bs.totalAssetsCents)}
                                </td>
                              </tr>

                              <tr className="kb-report__section">
                                <td colSpan={2}>Liabilities</td>
                              </tr>
                              {bs.liabilities.map((r) => (
                                <tr
                                  key={r.accountId}
                                  className="kb-report__row--drill"
                                  onClick={() =>
                                    setDrill({
                                      accountId: r.accountId,
                                      label: `${r.code} ${r.name}`,
                                      from: from || (bs.asOf ?? to),
                                      to: bs.asOf ?? to,
                                    })
                                  }
                                  title="Click to drill into this account"
                                >
                                  <td>
                                    <span className="kb-report__code">{r.code}</span> {r.name}
                                  </td>
                                  <td className="kb-report__amt">{fmt(r.amountCents)}</td>
                                </tr>
                              ))}
                              <tr
                                className="kb-report__subtotal kb-report__row--drill"
                                onClick={() =>
                                  setDrill({
                                    accountIds: bs.liabilities.map((r) => r.accountId),
                                    label: 'Total liabilities',
                                    from: from || (bs.asOf ?? to),
                                    to: bs.asOf ?? to,
                                  })
                                }
                                title="Click to see every line in liabilities"
                              >
                                <td>Total liabilities</td>
                                <td className="kb-report__amt">
                                  {fmt(bs.totalLiabilitiesCents)}
                                </td>
                              </tr>

                              <tr className="kb-report__section">
                                <td colSpan={2}>Equity</td>
                              </tr>
                              {bs.equity.map((r) => (
                                <tr
                                  key={r.accountId}
                                  className="kb-report__row--drill"
                                  onClick={() =>
                                    setDrill({
                                      accountId: r.accountId,
                                      label: `${r.code} ${r.name}`,
                                      from: from || (bs.asOf ?? to),
                                      to: bs.asOf ?? to,
                                    })
                                  }
                                  title="Click to drill into this account"
                                >
                                  <td>
                                    <span className="kb-report__code">{r.code}</span> {r.name}
                                  </td>
                                  <td className="kb-report__amt">{fmt(r.amountCents)}</td>
                                </tr>
                              ))}
                              <tr
                                className="kb-report__subtotal kb-report__row--drill"
                                onClick={() =>
                                  setDrill({
                                    accountIds: bs.equity.map((r) => r.accountId),
                                    label: 'Total equity',
                                    from: from || (bs.asOf ?? to),
                                    to: bs.asOf ?? to,
                                  })
                                }
                                title="Click to see every line in equity"
                              >
                                <td>Total equity</td>
                                <td className="kb-report__amt">
                                  {fmt(bs.totalEquityCents)}
                                </td>
                              </tr>

                              <tr className="kb-report__total">
                                <td>Liabilities + Equity</td>
                                <td className="kb-report__amt">
                                  {fmt(bs.totalLiabilitiesCents + bs.totalEquityCents)}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        )
                      )}
                    </Card>
                  </div>
                )}

                {(view === 'all' || view === 'ar') && (
                  <div style={{ marginTop: '1.25rem' }}>
                    <ArAgingCard ar={ar} />
                  </div>
                )}

                {(view === 'all' || view === 'cf') && (
                  <div style={{ marginTop: '1.25rem' }}>
                    <Card>
                      <CardTitle>Statement of Cash Flows</CardTitle>
                      <ExportButtons
                        name="cash-flow"
                        params={{ from, to, letterhead }}
                        disabled={!cf}
                      />
                      <CardSubtitle>
                        {cf ? `${cf.from} to ${cf.to}` : `${from} to ${to}`} — Indirect method
                      </CardSubtitle>
                      {cf &&
                      cf.operating.items.length === 0 &&
                      cf.investing.items.length === 0 &&
                      cf.financing.items.length === 0 &&
                      cf.netIncomeCents === 0 ? (
                        <p className="kb-muted" style={{ marginTop: '1rem' }}>
                          No activity in this period yet.
                        </p>
                      ) : cf ? (
                        <table className="kb-report">
                          <tbody>
                            <tr className="kb-report__section">
                              <td colSpan={2}>Operating Activities</td>
                            </tr>
                            <tr>
                              <td>Net income</td>
                              <td className="kb-report__amt">{fmt(cf.netIncomeCents)}</td>
                            </tr>
                            {cf.operating.items.map((it) => (
                              <tr key={`op-${it.code}`}>
                                <td>
                                  <span className="kb-report__code">{it.code}</span> {it.name}
                                </td>
                                <td className="kb-report__amt">{fmt(it.amountCents)}</td>
                              </tr>
                            ))}
                            <tr className="kb-report__subtotal">
                              <td>Net cash from operating activities</td>
                              <td className="kb-report__amt">
                                {fmt(cf.operating.totalCents)}
                              </td>
                            </tr>

                            <tr className="kb-report__section">
                              <td colSpan={2}>Investing Activities</td>
                            </tr>
                            {cf.investing.items.length === 0 && (
                              <tr>
                                <td className="kb-muted">(no investing activity)</td>
                                <td className="kb-report__amt">—</td>
                              </tr>
                            )}
                            {cf.investing.items.map((it) => (
                              <tr key={`inv-${it.code}`}>
                                <td>
                                  <span className="kb-report__code">{it.code}</span> {it.name}
                                </td>
                                <td className="kb-report__amt">{fmt(it.amountCents)}</td>
                              </tr>
                            ))}
                            <tr className="kb-report__subtotal">
                              <td>Net cash from investing activities</td>
                              <td className="kb-report__amt">
                                {fmt(cf.investing.totalCents)}
                              </td>
                            </tr>

                            <tr className="kb-report__section">
                              <td colSpan={2}>Financing Activities</td>
                            </tr>
                            {cf.financing.items.length === 0 && (
                              <tr>
                                <td className="kb-muted">(no financing activity)</td>
                                <td className="kb-report__amt">—</td>
                              </tr>
                            )}
                            {cf.financing.items.map((it) => (
                              <tr key={`fin-${it.code}`}>
                                <td>
                                  <span className="kb-report__code">{it.code}</span> {it.name}
                                </td>
                                <td className="kb-report__amt">{fmt(it.amountCents)}</td>
                              </tr>
                            ))}
                            <tr className="kb-report__subtotal">
                              <td>Net cash from financing activities</td>
                              <td className="kb-report__amt">
                                {fmt(cf.financing.totalCents)}
                              </td>
                            </tr>

                            <tr className="kb-report__total">
                              <td>Net change in cash</td>
                              <td className="kb-report__amt">
                                {fmt(cf.netChangeInCashCents)}
                              </td>
                            </tr>
                            <tr>
                              <td>Cash at beginning of period</td>
                              <td className="kb-report__amt">
                                {fmt(cf.beginningCashCents)}
                              </td>
                            </tr>
                            <tr className="kb-report__total">
                              <td>Cash at end of period</td>
                              <td className="kb-report__amt">{fmt(cf.endingCashCents)}</td>
                            </tr>
                          </tbody>
                        </table>
                      ) : null}
                    </Card>
                  </div>
                )}

                {(view === 'all' || view === 'gl') && (
                  /* General ledger — all accounts summary */
                  <div style={{ marginTop: '1.25rem' }}>
                    <Card>
                      <CardTitle>General Ledger — all accounts</CardTitle>
                      <ExportButtons
                        name="gl-summary"
                        params={{ from, to, letterhead }}
                        disabled={!glSum}
                      />
                      <CardSubtitle>
                        Opening balance + period activity + closing, every active account on one
                        page.
                        {glSum && (
                          <>
                            {' '}
                            · {glSum.from} to {glSum.to}
                          </>
                        )}
                      </CardSubtitle>
                      {glSum && glSum.accounts.length === 0 ? (
                        <p className="kb-muted" style={{ marginTop: '1rem' }}>
                          No accounts yet — finish onboarding first.
                        </p>
                      ) : (
                        glSum && (
                          <table className="kb-report">
                            <thead>
                              <tr>
                                <th>Account</th>
                                <th className="kb-report__amt">Opening</th>
                                <th className="kb-report__amt">Debit</th>
                                <th className="kb-report__amt">Credit</th>
                                <th className="kb-report__amt">Closing</th>
                              </tr>
                            </thead>
                            <tbody>
                              {glSum.accounts.map((a) => (
                                <tr key={a.accountId}>
                                  <td>
                                    <span className="kb-report__code">{a.code}</span> {a.name}
                                  </td>
                                  <td className="kb-report__amt">{fmt(a.openingCents)}</td>
                                  <td className="kb-report__amt">
                                    {a.periodDebitCents ? fmt(a.periodDebitCents) : ''}
                                  </td>
                                  <td className="kb-report__amt">
                                    {a.periodCreditCents ? fmt(a.periodCreditCents) : ''}
                                  </td>
                                  <td className="kb-report__amt">{fmt(a.closingCents)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )
                      )}
                    </Card>
                  </div>
                )}

                {(view === 'all' || view === 'gl') && (
                  <div style={{ marginTop: '1.25rem' }}>
                    <GlDetailSection />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
      {drill && <DrillDownPanel target={drill} onClose={() => setDrill(null)} />}
    </Layout>
  );
}

type ReportName =
  | 'income-statement'
  | 'trial-balance'
  | 'balance-sheet'
  | 'cash-flow'
  | 'gl-summary'
  | 'all';

/**
 * Inline three-format download menu rendered into the top-right of each
 * report card. PDF for printing / sending to clients, XLSX for the books-
 * literate user who wants to dig further, CSV for anyone scripting
 * downstream. A short busy state on the active format keeps double-clicks
 * from firing two downloads.
 */
function ExportButtons({
  name,
  params,
  disabled,
}: {
  name: ReportName;
  params: { from?: string; to?: string; asOf?: string; letterhead?: boolean };
  disabled?: boolean;
}) {
  const [busy, setBusy] = useState<null | 'pdf' | 'xlsx' | 'csv'>(null);
  const [err, setErr] = useState<string | null>(null);

  async function go(format: 'pdf' | 'xlsx' | 'csv') {
    setBusy(format);
    setErr(null);
    try {
      await api.downloadReport(name, format, params);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : `${format} download failed.`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        gap: 6,
        marginTop: 8,
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      <span className="kb-muted" style={{ fontSize: 12 }}>
        Download:
      </span>
      {(['pdf', 'xlsx', 'csv'] as const).map((f) => (
        <Button
          key={f}
          size="sm"
          variant="ghost"
          disabled={disabled || busy !== null}
          onClick={() => void go(f)}
        >
          {busy === f ? '…' : f.toUpperCase()}
        </Button>
      ))}
      {err && (
        <span role="alert" className="kb-form-error" style={{ fontSize: 12, marginLeft: 8 }}>
          {err}
        </span>
      )}
    </div>
  );
}

/**
 * Row-per-month summary tables for the four primary statements. Renders
 * one card per statement on the "All" tab when the chosen Period spans
 * more than a single calendar month — Brickstone specifically asked for
 * Jan, Feb, ... Jun individually plus a Total row when she selects a
 * multi-month range.
 *
 * Each table is intentionally small (5 columns max) so the entire
 * dashboard fits on a laptop screen without horizontal scroll. For
 * per-account detail the user switches to the single-statement tabs
 * (Profit & Loss, Trial Balance, etc.) — those keep the full layout.
 */
/**
 * Comparative columnar P&L — one row per account, one column per month
 * in the chosen window, plus a Total column on the right. Sections are
 * Revenue / Cost of Revenue (conditional) / Gross Profit (conditional) /
 * Operating Expenses / Net Income. Standard CPA review format for
 * multi-month statements.
 *
 * Long ranges (e.g. a full year) produce 13+ columns — the wrapper has
 * overflow-x:auto so the table scrolls horizontally instead of wrapping
 * or clipping.
 */
/**
 * Free-form notes attached to a specific report instance (engagement +
 * report kind + period). Brickstone types whatever observations the
 * reviewer wants to leave on the file — "verify Cash App transfers",
 * "personal expenses on business card", whatever — and the body is
 * preserved verbatim through to the PDF / XLSX / CSV exports so the
 * deliverable carries the same notes a CPA would write at the bottom
 * of a printed draft.
 *
 * Saves on blur — the user types, clicks away, and the textarea
 * persists. A subtle status row underneath reads "Saved" / "Saving…"
 * so the user knows the write landed. Empty body clears the row.
 */
function ReportNotes({
  reportKind,
  from,
  to,
}: {
  reportKind: 'income_statement' | 'balance_sheet' | 'cash_flow' | 'trial_balance';
  from: string;
  to: string;
}) {
  const [body, setBody] = useState<string>('');
  const [lastSavedBody, setLastSavedBody] = useState<string>('');
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'saving' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  // Fetch (or refetch when the date window changes) — notes are scoped
  // per (engagement, kind, from, to), so a Jan view and a Jan-Mar view
  // have independent bodies.
  useEffect(() => {
    if (!from || !to) return;
    let alive = true;
    setError(null);
    void api
      .getReportNote({ reportKind, from, to })
      .then((r) => {
        if (!alive) return;
        setBody(r.note?.body ?? '');
        setLastSavedBody(r.note?.body ?? '');
        setUpdatedAt(r.note?.updatedAt ?? null);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setError(err instanceof Error ? err.message : 'Could not load notes.');
      });
    return () => {
      alive = false;
    };
  }, [reportKind, from, to]);

  async function save() {
    if (body === lastSavedBody) return; // no change → no write
    setStatus('saving');
    setError(null);
    try {
      const r = await api.upsertReportNote({ reportKind, from, to, body });
      setLastSavedBody(body);
      setUpdatedAt(r.note?.updatedAt ?? null);
      setStatus('idle');
    } catch (err: unknown) {
      setStatus('error');
      const msg = err instanceof Error ? err.message : 'Could not save notes.';
      setError(msg);
      toast({ tone: 'error', message: msg });
    }
  }

  return (
    <div
      style={{
        marginTop: '1.25rem',
        paddingTop: '1rem',
        borderTop: '1px solid var(--kb-gray-200)',
      }}
    >
      <div className="kb-muted" style={{ fontSize: 13, marginBottom: '0.5rem', fontWeight: 600 }}>
        Reviewer notes
      </div>
      <textarea
        className="kb-input kb-input--textarea"
        style={{ width: '100%', minHeight: 120, fontFamily: 'inherit' }}
        value={body}
        onChange={(e) => setBody(e.currentTarget.value)}
        onBlur={() => void save()}
        placeholder="Observations, questions for the client, items needing source documents — the notes ride along with every export."
      />
      <div
        style={{
          display: 'flex',
          gap: 12,
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 4,
          fontSize: 12,
          color: 'var(--kb-gray-400)',
        }}
      >
        <span>
          {status === 'saving' ? (
            'Saving…'
          ) : error ? (
            <span style={{ color: 'var(--kb-red-600)' }}>{error}</span>
          ) : updatedAt ? (
            `Saved ${new Date(updatedAt).toLocaleString()}`
          ) : (
            'Not saved yet'
          )}
        </span>
        {body !== lastSavedBody && status !== 'saving' && (
          <span style={{ fontStyle: 'italic' }}>Unsaved changes — click outside to save</span>
        )}
      </div>
    </div>
  );
}

function ArAgingCard({ ar }: { ar: ArAgingResp | null }) {
  const fmt = useMoneyFormatter();
  const [openCustomers, setOpenCustomers] = useState<Record<string, boolean>>({});
  if (!ar) {
    return (
      <Card>
        <CardTitle>A/R Aging</CardTitle>
        <CardSubtitle>Loading…</CardSubtitle>
      </Card>
    );
  }
  const total = ar.grandTotalCents;
  return (
    <Card>
      <CardTitle>A/R Aging</CardTitle>
      <CardSubtitle>
        As of {ar.asOf} — open invoices grouped by customer + bucketed by age.
      </CardSubtitle>

      {total === 0 ? (
        <p className="kb-muted" style={{ marginTop: '1rem' }}>
          No open invoices.
        </p>
      ) : (
        <table className="kb-report" style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Customer</th>
              {AR_BUCKETS.map((b) => (
                <th key={b.key} className="kb-report__amt">
                  {b.label}
                </th>
              ))}
              <th className="kb-report__amt">Total</th>
            </tr>
          </thead>
          <tbody>
            {ar.customers.map((c) => {
              const open = !!openCustomers[c.customerId];
              return (
                <Fragment key={c.customerId}>
                  <tr
                    className="kb-report__row--drill"
                    onClick={() =>
                      setOpenCustomers((p) => ({ ...p, [c.customerId]: !p[c.customerId] }))
                    }
                    title="Click to expand invoices"
                  >
                    <td>
                      <span style={{ display: 'inline-block', width: 14 }}>{open ? '▾' : '▸'}</span>
                      {c.customerName}
                    </td>
                    {AR_BUCKETS.map((b) => (
                      <td key={b.key} className="kb-report__amt">
                        {c.buckets[b.key] ? fmt(c.buckets[b.key]!) : '—'}
                      </td>
                    ))}
                    <td className="kb-report__amt">{fmt(c.balanceCents)}</td>
                  </tr>
                  {open &&
                    c.invoices.map((inv) => (
                      <tr key={inv.invoiceId} style={{ background: 'var(--kb-gray-50, #f8fafc)' }}>
                        <td style={{ paddingLeft: 32, fontSize: 13 }}>
                          {inv.docNumber} · due {inv.dueDate}
                          {inv.daysOverdue > 0 && (
                            <span
                              className="kb-pill kb-pill--warn"
                              style={{ marginLeft: 8, fontSize: 11 }}
                            >
                              {inv.daysOverdue}d overdue
                            </span>
                          )}
                        </td>
                        {AR_BUCKETS.map((b) => (
                          <td key={b.key} className="kb-report__amt" style={{ fontSize: 13 }}>
                            {inv.bucket === b.key ? fmt(inv.balanceCents) : ''}
                          </td>
                        ))}
                        <td className="kb-report__amt" style={{ fontSize: 13 }}>
                          {fmt(inv.balanceCents)}
                        </td>
                      </tr>
                    ))}
                </Fragment>
              );
            })}
            <tr className="kb-report__total">
              <td>Grand total</td>
              {AR_BUCKETS.map((b) => (
                <td key={b.key} className="kb-report__amt">
                  {ar.grandTotals[b.key] ? fmt(ar.grandTotals[b.key]!) : '—'}
                </td>
              ))}
              <td className="kb-report__amt">{fmt(ar.grandTotalCents)}</td>
            </tr>
          </tbody>
        </table>
      )}
    </Card>
  );
}

function ColumnarPlCard({
  data,
  from,
  to,
  letterhead,
}: {
  data: ColumnarPlResp;
  from: string;
  to: string;
  letterhead: boolean;
}) {
  const fmt = useMoneyFormatter();
  const monthLabel = (ym: string) => {
    const [, m] = ym.split('-').map(Number);
    const names = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    return `${names[(m ?? 1) - 1]} ${ym.slice(0, 4)}`;
  };

  const revenueRows = data.rows.filter((r) => r.section === 'revenue');
  const cogsRows = data.rows.filter((r) => r.section === 'cost_of_revenue');
  const expenseRows = data.rows.filter((r) => r.section === 'expense');
  const hasCogs = cogsRows.length > 0 || data.totalsCostOfRevenue.totalCents !== 0;

  const renderAmountCells = (values: number[], total: number, opts?: { bold?: boolean }) => (
    <>
      {values.map((v, i) => (
        <td
          key={i}
          className="kb-report__amt"
          style={{ fontVariantNumeric: 'tabular-nums', fontWeight: opts?.bold ? 600 : undefined }}
        >
          {fmt(v)}
        </td>
      ))}
      <td
        className="kb-report__amt"
        style={{
          fontVariantNumeric: 'tabular-nums',
          fontWeight: opts?.bold ? 700 : 600,
          borderLeft: '1px solid var(--kb-gray-200)',
        }}
      >
        {fmt(total)}
      </td>
    </>
  );

  return (
    <div style={{ marginTop: '1.25rem' }}>
      <Card>
        <CardTitle>Profit &amp; Loss — by month (columnar)</CardTitle>
        <CardSubtitle>
          One row per account, one column per month, a Total column on the right. The single-
          statement tabs above hold the single-period detail; this card is the comparative view a
          reviewer hands off for multi-month sign-off.
        </CardSubtitle>
        <div style={{ overflowX: 'auto', marginTop: '0.75rem' }}>
          <table className="kb-report" style={{ minWidth: 'max-content' }}>
            <thead>
              <tr>
                <th
                  style={{ position: 'sticky', left: 0, background: 'var(--kb-gray-50, #f8fafc)' }}
                >
                  Account
                </th>
                {data.months.map((m) => (
                  <th key={m} className="kb-report__amt" style={{ whiteSpace: 'nowrap' }}>
                    {monthLabel(m)}
                  </th>
                ))}
                <th
                  className="kb-report__amt"
                  style={{ borderLeft: '1px solid var(--kb-gray-200)', whiteSpace: 'nowrap' }}
                >
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="kb-report__section">
                <td colSpan={data.months.length + 2}>REVENUE</td>
              </tr>
              {revenueRows.length === 0 ? (
                <tr>
                  <td className="kb-muted">No revenue activity in this period</td>
                  {data.months.map((m) => (
                    <td key={m} className="kb-report__amt">
                      —
                    </td>
                  ))}
                  <td className="kb-report__amt">—</td>
                </tr>
              ) : (
                revenueRows.map((row) => (
                  <tr key={row.accountId}>
                    <td>
                      <span className="kb-report__code">{row.code}</span> {row.name}
                    </td>
                    {renderAmountCells(row.monthlyCents, row.totalCents)}
                  </tr>
                ))
              )}
              <tr className="kb-report__subtotal">
                <td>Total Revenue</td>
                {renderAmountCells(data.totalsRevenue.monthlyCents, data.totalsRevenue.totalCents, {
                  bold: true,
                })}
              </tr>

              {hasCogs && (
                <>
                  <tr className="kb-report__section">
                    <td colSpan={data.months.length + 2}>COST OF REVENUE</td>
                  </tr>
                  {cogsRows.map((row) => (
                    <tr key={row.accountId}>
                      <td>
                        <span className="kb-report__code">{row.code}</span> {row.name}
                      </td>
                      {renderAmountCells(row.monthlyCents, row.totalCents)}
                    </tr>
                  ))}
                  <tr className="kb-report__subtotal">
                    <td>Total Cost of Revenue</td>
                    {renderAmountCells(
                      data.totalsCostOfRevenue.monthlyCents,
                      data.totalsCostOfRevenue.totalCents,
                      { bold: true },
                    )}
                  </tr>
                  <tr className="kb-report__total">
                    <td>Gross Profit</td>
                    {renderAmountCells(data.grossProfit.monthlyCents, data.grossProfit.totalCents, {
                      bold: true,
                    })}
                  </tr>
                </>
              )}

              <tr className="kb-report__section">
                <td colSpan={data.months.length + 2}>OPERATING EXPENSES</td>
              </tr>
              {expenseRows.length === 0 ? (
                <tr>
                  <td className="kb-muted">No expense activity in this period</td>
                  {data.months.map((m) => (
                    <td key={m} className="kb-report__amt">
                      —
                    </td>
                  ))}
                  <td className="kb-report__amt">—</td>
                </tr>
              ) : (
                expenseRows.map((row) => (
                  <tr key={row.accountId}>
                    <td>
                      <span className="kb-report__code">{row.code}</span> {row.name}
                    </td>
                    {renderAmountCells(row.monthlyCents, row.totalCents)}
                  </tr>
                ))
              )}
              <tr className="kb-report__subtotal">
                <td>Total Operating Expenses</td>
                {renderAmountCells(
                  data.totalsExpenses.monthlyCents,
                  data.totalsExpenses.totalCents,
                  { bold: true },
                )}
              </tr>

              <tr className="kb-report__total">
                <td>Net Income (Loss)</td>
                {renderAmountCells(data.netIncome.monthlyCents, data.netIncome.totalCents, {
                  bold: true,
                })}
              </tr>
            </tbody>
          </table>
        </div>
        <ColumnarPlDownload from={from} to={to} letterhead={letterhead} />
      </Card>
    </div>
  );
}

/**
 * One-button XLSX download for the columnar P&L. Lives only on this card
 * because the columnar shape is XLSX-only for now — the wide table doesn't
 * fit a portrait PDF and the user's STARLINX reference is itself a
 * spreadsheet. PDF/CSV variants are a planned follow-up.
 */
function ColumnarPlDownload({
  from,
  to,
  letterhead,
}: {
  from: string;
  to: string;
  letterhead: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function go() {
    setBusy(true);
    setErr(null);
    try {
      await api.downloadReport('income-statement-by-month', 'xlsx', { from, to, letterhead });
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'XLSX download failed.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div
      style={{
        display: 'flex',
        gap: 6,
        marginTop: 12,
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      <span className="kb-muted" style={{ fontSize: 12 }}>
        Download:
      </span>
      <Button size="sm" variant="ghost" disabled={busy} onClick={() => void go()}>
        {busy ? '…' : 'XLSX'}
      </Button>
      {err && (
        <span role="alert" className="kb-form-error" style={{ fontSize: 12, marginLeft: 8 }}>
          {err}
        </span>
      )}
    </div>
  );
}

function MonthlySummaryCards({ monthly }: { monthly: MonthlySummaryResp }) {
  const fmt = useMoneyFormatter();
  const months = monthly.months;
  const totals = months.reduce(
    (acc, m) => ({
      revenueCents: acc.revenueCents + m.revenueCents,
      expensesCents: acc.expensesCents + m.expensesCents,
      netIncomeCents: acc.netIncomeCents + m.netIncomeCents,
      operatingCfCents: acc.operatingCfCents + m.operatingCfCents,
      investingCfCents: acc.investingCfCents + m.investingCfCents,
      financingCfCents: acc.financingCfCents + m.financingCfCents,
      netChangeInCashCents: acc.netChangeInCashCents + m.netChangeInCashCents,
    }),
    {
      revenueCents: 0,
      expensesCents: 0,
      netIncomeCents: 0,
      operatingCfCents: 0,
      investingCfCents: 0,
      financingCfCents: 0,
      netChangeInCashCents: 0,
    },
  );
  const monthLabel = (ym: string) => {
    const [, m] = ym.split('-').map(Number);
    const names = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    return `${names[(m ?? 1) - 1]} ${ym.slice(0, 4)}`;
  };

  return (
    <>
      <div style={{ marginTop: '1.25rem' }}>
        <Card>
          <CardTitle>Profit &amp; Loss — by month</CardTitle>
          <CardSubtitle>
            One row per month for the selected period, plus a Total row at the bottom.
          </CardSubtitle>
          <table className="kb-report">
            <thead>
              <tr>
                <th>Month</th>
                <th className="kb-report__amt">Revenue</th>
                <th className="kb-report__amt">Expenses</th>
                <th className="kb-report__amt">Net Income</th>
              </tr>
            </thead>
            <tbody>
              {months.map((m) => (
                <tr key={m.month}>
                  <td>{monthLabel(m.month)}</td>
                  <td className="kb-report__amt">{fmt(m.revenueCents)}</td>
                  <td className="kb-report__amt">{fmt(m.expensesCents)}</td>
                  <td className="kb-report__amt">{fmt(m.netIncomeCents)}</td>
                </tr>
              ))}
              <tr className="kb-report__total">
                <td>Total</td>
                <td className="kb-report__amt">{fmt(totals.revenueCents)}</td>
                <td className="kb-report__amt">{fmt(totals.expensesCents)}</td>
                <td className="kb-report__amt">{fmt(totals.netIncomeCents)}</td>
              </tr>
            </tbody>
          </table>
        </Card>
      </div>

      <div style={{ marginTop: '1.25rem' }}>
        <Card>
          <CardTitle>Balance Sheet — by month-end</CardTitle>
          <CardSubtitle>
            Position as of each month's end. A Total row isn't meaningful here — Balance Sheet
            figures don't sum across periods.
          </CardSubtitle>
          <table className="kb-report">
            <thead>
              <tr>
                <th>Month-end</th>
                <th className="kb-report__amt">Total Assets</th>
                <th className="kb-report__amt">Total Liabilities</th>
                <th className="kb-report__amt">Total Equity</th>
              </tr>
            </thead>
            <tbody>
              {months.map((m) => (
                <tr key={m.month}>
                  <td>{m.monthEnd}</td>
                  <td className="kb-report__amt">{fmt(m.totalAssetsCents)}</td>
                  <td className="kb-report__amt">{fmt(m.totalLiabilitiesCents)}</td>
                  <td className="kb-report__amt">{fmt(m.totalEquityCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      <div style={{ marginTop: '1.25rem' }}>
        <Card>
          <CardTitle>Cash Flows — by month</CardTitle>
          <CardSubtitle>
            Operating, investing, and financing activity per month, plus the net change in cash.
          </CardSubtitle>
          <table className="kb-report">
            <thead>
              <tr>
                <th>Month</th>
                <th className="kb-report__amt">Operating</th>
                <th className="kb-report__amt">Investing</th>
                <th className="kb-report__amt">Financing</th>
                <th className="kb-report__amt">Net Change</th>
              </tr>
            </thead>
            <tbody>
              {months.map((m) => (
                <tr key={m.month}>
                  <td>{monthLabel(m.month)}</td>
                  <td className="kb-report__amt">{fmt(m.operatingCfCents)}</td>
                  <td className="kb-report__amt">{fmt(m.investingCfCents)}</td>
                  <td className="kb-report__amt">{fmt(m.financingCfCents)}</td>
                  <td className="kb-report__amt">{fmt(m.netChangeInCashCents)}</td>
                </tr>
              ))}
              <tr className="kb-report__total">
                <td>Total</td>
                <td className="kb-report__amt">{fmt(totals.operatingCfCents)}</td>
                <td className="kb-report__amt">{fmt(totals.investingCfCents)}</td>
                <td className="kb-report__amt">{fmt(totals.financingCfCents)}</td>
                <td className="kb-report__amt">{fmt(totals.netChangeInCashCents)}</td>
              </tr>
            </tbody>
          </table>
        </Card>
      </div>

      <div style={{ marginTop: '1.25rem' }}>
        <Card>
          <CardTitle>Trial Balance — by month-end</CardTitle>
          <CardSubtitle>
            Cumulative debit and credit totals as of each month's end. Each row should be in balance
            (debits = credits).
          </CardSubtitle>
          <table className="kb-report">
            <thead>
              <tr>
                <th>Month-end</th>
                <th className="kb-report__amt">Total Debit</th>
                <th className="kb-report__amt">Total Credit</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {months.map((m) => (
                <tr key={m.month}>
                  <td>{m.monthEnd}</td>
                  <td className="kb-report__amt">{fmt(m.totalDebitCents)}</td>
                  <td className="kb-report__amt">{fmt(m.totalCreditCents)}</td>
                  <td>
                    {m.balanced ? (
                      <span className="kb-pill kb-pill--ok">Balanced</span>
                    ) : (
                      <span className="kb-pill kb-pill--warn">Out of balance</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </>
  );
}

function GlDetailSection() {
  const fmt = useMoneyFormatter();
  const [accounts, setAccounts] = useState<GlAccount[]>([]);
  const [accountId, setAccountId] = useState<string>('');
  const [from, setFrom] = useState<string>(firstOfMonth());
  const [to, setTo] = useState<string>(todayIso());
  const [data, setData] = useState<GlResp | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .getAccounts()
      .then((r) => {
        const sorted = [...r.accounts].sort((a, b) => a.code.localeCompare(b.code));
        setAccounts(sorted);
        if (sorted[0]) setAccountId(sorted[0].id);
      })
      .catch(() => undefined);
  }, []);

  async function run() {
    if (!accountId) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.getGlDetail({ accountId, from, to });
      setData(r);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not load the GL.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardTitle>General Ledger detail</CardTitle>
      <CardSubtitle>
        One account at a time — opening balance pulled forward, every line with a running balance.
      </CardSubtitle>

      <div
        style={{
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          marginTop: '0.75rem',
        }}
      >
        <Field label="Account">
          <Select value={accountId} onChange={(e) => setAccountId(e.currentTarget.value)}>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.code} — {a.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="From">
          <Input type="date" value={from} onChange={(e) => setFrom(e.currentTarget.value)} />
        </Field>
        <Field label="To">
          <Input type="date" value={to} onChange={(e) => setTo(e.currentTarget.value)} />
        </Field>
        <Button size="sm" disabled={busy || !accountId} onClick={run}>
          {busy ? 'Loading…' : 'Run'}
        </Button>
      </div>

      {error && (
        <div role="alert" className="kb-form-error" style={{ marginTop: 10 }}>
          {error}
        </div>
      )}

      {data && (
        <table className="kb-report" style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th className="kb-report__amt">Debit</th>
              <th className="kb-report__amt">Credit</th>
              <th className="kb-report__amt">Balance</th>
            </tr>
          </thead>
          <tbody>
            <tr className="kb-report__section">
              <td colSpan={4}>Opening balance</td>
              <td className="kb-report__amt">{fmt(data.openingBalanceCents)}</td>
            </tr>
            {data.lines.length === 0 ? (
              <tr>
                <td colSpan={5} className="kb-muted">
                  No activity in this range.
                </td>
              </tr>
            ) : (
              data.lines.map((l) => (
                <tr key={l.entryId + '|' + l.postedAt}>
                  <td>{new Date(l.postedAt).toLocaleDateString()}</td>
                  <td>
                    {l.description}
                    {l.memo ? <span className="kb-muted"> — {l.memo}</span> : null}
                  </td>
                  <td className="kb-report__amt">
                    {l.debitCents ? fmt(l.debitCents) : ''}
                  </td>
                  <td className="kb-report__amt">
                    {l.creditCents ? fmt(l.creditCents) : ''}
                  </td>
                  <td className="kb-report__amt">
                    {/* The standalone GL Detail card is single-account by
                        design (the user picks one account), so balanceCents
                        is always a number here. But the API now models
                        multi-account drills too, so the type is nullable. */}
                    {l.balanceCents !== null ? fmt(l.balanceCents) : ''}
                  </td>
                </tr>
              ))
            )}
            <tr className="kb-report__total">
              <td colSpan={4}>Closing balance</td>
              <td className="kb-report__amt">{fmt(data.closingBalanceCents)}</td>
            </tr>
          </tbody>
        </table>
      )}
    </Card>
  );
}
